import React, { useState } from "react";
import {
  Box,
  Typography,
  Chip,
  TextField,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Paper,
  InputAdornment,
  IconButton,
  Button,
  Tooltip,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import CloseIcon from "@mui/icons-material/Close";
import baseURL from "../../config";
import axios from "axios";
import { toast } from "react-toastify";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const badgeStyle = {
  m: 0.5,
  fontWeight: "bold",
};

const SignOnModal = ({ signonDetails = [] }) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [openModal, setOpenModal] = useState(false);
  const [selectedDocuments, setSelectedDocuments] = useState([]);

  const filteredData = signonDetails.filter((item) => {
    const vesselName = item?.contract?.vesselName || "";
    const crewName = item?.contract?.crewName || "";
    return (
      vesselName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      crewName.toLowerCase().includes(searchTerm.toLowerCase())
    );
  });

  const handleOpenModal = (item) => {
    if (item?.data) {
      const documents = Object.entries(item.data)
        .filter(([key, value]) =>
          ["ImoCrewListFile", "cdcStamppageFile"].includes(key) && value
        )
        .map(([key, value]) => {
          let filePath = typeof value === "string" ? value : value?.filePath;

          if (!filePath) return null;

          // Add baseURL if not already a full URL
          if (!filePath.startsWith("http")) {
            filePath = `${baseURL}/${filePath.replace(/\\/g, "/")}`;
          }

          // Create a more user-friendly document name
          let displayName = key;
          if (key === "ImoCrewListFile") displayName = "IMO Crew List";
          if (key === "cdcStamppageFile") displayName = "CDC Stamp Page";

          return {
            fileName: displayName,
            originalKey: key,
            filePath: filePath,
          };
        })
        .filter(Boolean);

      setSelectedDocuments(documents);
      setOpenModal(true);
    }
  };

  const handleFileUpload = async (file, item) => {
    if (!file || !item) return;

    const formData = new FormData();
    formData.append("ssDocument", file);
    formData.append("crewid", item?.contract?.crewdetails?._id || "");
    formData.append("crewinagentId", item?.contract?.crewingAgent || "");
    formData.append("contractId", item?.contract?._id || "");
    formData.append("crewName", item?.contract?.crewName || "");
    formData.append("type", "signon");

    try {
      const token = localStorage.getItem("token"); // Or however you store your JWT token

      const response = await axios.post(
        `${baseURL}/api/crewingAgent/ssDocumentsForCrewSignOnSignOff`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
            Authorization: `Bearer ${token}`, // Include token here
          },
        }
      );

      toast.success("Document uploaded successfully!");
      setTimeout(() => {
        window.location.reload();
      }, 2000);
      // console.log(response.data);
    } catch (error) {
      const message =
        error.response?.data?.error || "An error occurred during file upload.";
      toast.error(message);
      console.error("Upload error:", error);
    }
  };

  return (
    <Box sx={{ mt: 5, px: 2 }}>
      <Paper
        elevation={3}
        sx={{
          p: 1,
          bgcolor: "#C30B12",
          color: "white",
          display: "flex",
          alignItems: "center",
          position: "relative",
        }}
      >
        <TextField
          placeholder="Search by Vessel Name or Crew"
          variant="outlined"
          size="small"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon color="primary" />
              </InputAdornment>
            ),
          }}
          sx={{
            bgcolor: "#f5f5f5",
            borderRadius: 2,
            width: "280px",
          }}
        />

        <Typography
          variant="h6"
          sx={{
            mx: "auto",
            textAlign: "center",
            flex: 1,
            fontWeight: "bold",
            color: "#f5f5f5",
          }}
        >
          Sign On Details
        </Typography>

        <Box sx={{ width: 300 }} />
      </Paper>

      <Paper elevation={3} sx={{ p: 2 }}>
        {filteredData.length > 0 ? (
          <Table stickyHeader>
            <TableHead>
              <TableRow sx={{ bgcolor: "#f5f5f5" }}>
                <TableCell sx={{ fontWeight: "bold" }}>Sr. No.</TableCell>
                <TableCell sx={{ fontWeight: "bold" }}>Contract Details</TableCell>
                <TableCell sx={{ fontWeight: "bold" }} align="center"> CDC / IMO</TableCell>
                <TableCell sx={{ fontWeight: "bold" }} align="center">Action</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredData.map((item, index) => {
                const signOnDate = item?.data?.date
                  ? new Date(item.data.date).toLocaleDateString("en-GB")
                  : "N/A";

                return (
                  <TableRow key={item._id || index}>
                    <TableCell>{index + 1}</TableCell>
                    <TableCell>{console.log("signon", item)}
                      <Typography fontWeight="bold">
                        Vessel Name: {item?.contract?.vesselName || "N/A"}
                      </Typography>
                      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mt: 1 }}>
                        <Chip label={`Crew: ${item?.contract?.crewName || "N/A"}`} color="primary" sx={badgeStyle} />
                        <Chip label={`Indos No.: ${item.contract?.crewdetails?.indosno || "N/A"}`} sx={{ ...badgeStyle, backgroundColor: "#ffeb3b", color: "#000" }} />
                        <Chip label={`Sign On Date: ${signOnDate}`} sx={{ ...badgeStyle, backgroundColor: "#ffe0b2", color: "#e65100" }} />
                      </Box>
                    </TableCell>
                    <TableCell align="center">
                      {(item?.data?.ImoCrewListFile || item?.data?.cdcStamppageFile) ? (
                        <IconButton onClick={() => handleOpenModal(item)}>
                          <PictureAsPdfIcon sx={{ color: "red" }} />
                        </IconButton>
                      ) : (
                        "-"
                      )}
                    </TableCell>
                    <TableCell align="center">
                      {item?.ssDocumentRecords?.[0]?.signon?.[0]?.ssDocument?.filePath ? (
                        <Tooltip title="SS Document" arrow placement="top">
                          <IconButton
                            href={`${baseURL}/${item.ssDocumentRecords[0].signon[0].ssDocument.filePath.replace(/\\/g, "/")}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            size="small"
                          >
                            <PictureAsPdfIcon sx={{ color: "green" }} />
                          </IconButton>
                        </Tooltip>
                      ) : (
                        <Tooltip title="Upload Screenshot" arrow placement="top">
                          <IconButton
                            component="label"
                            size="small"
                          >
                            <input
                              type="file"
                              accept="application/pdf"
                              hidden
                              onChange={(e) => {
                                const file = e.target.files[0];
                                if (file) {
                                  handleFileUpload(file, item);
                                }
                                e.target.value = null;
                              }}
                            />
                            <CloudUploadIcon sx={{ color: "#757575" }} />
                          </IconButton>
                        </Tooltip>
                      )}
                    </TableCell>


                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        ) : (
          <Typography textAlign="center" sx={{ mt: 3 }}>
            No matching Sign On data found.
          </Typography>
        )}
      </Paper>

      {/* Modal for document links */}
      {openModal && (
        <Box
          sx={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100vw",
            height: "100vh",
            backgroundColor: "rgba(0,0,0,0.5)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 9999,
          }}
          onClick={() => setOpenModal(false)}
        >
          <Box
            sx={{
              backgroundColor: "white",
              borderRadius: 1,
              minWidth: 500,
              maxWidth: "80%",
              maxHeight: "80vh",
              overflowY: "auto",
              position: "relative",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <Box sx={{
              p: 1.5,
              bgcolor: "primary.main",
              color: "white",
              display: "flex",
              justifyContent: "center",
              position: "relative",
              borderTopLeftRadius: 4,
              borderTopRightRadius: 4
            }}>
              <Typography variant="h6" sx={{ fontWeight: "bold" }}>
                Documents
              </Typography>
              <IconButton
                sx={{
                  position: 'absolute',
                  right: 8,
                  top: '50%',
                  transform: 'translateY(-50%)',
                  color: 'white'
                }}
                onClick={() => setOpenModal(false)}
              >
                <CloseIcon />
              </IconButton>
            </Box>

            <Box sx={{ p: 2 }}>
              {selectedDocuments.map((doc, idx) => (
                <Box key={idx} sx={{
                  mb: 2,
                  p: 2,
                  border: "1px solid #e0e0e0",
                  borderRadius: 1,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between"
                }}>
                  <Box sx={{ display: "flex", alignItems: "center" }}>
                    <PictureAsPdfIcon sx={{ color: "red", mr: 2 }} />
                    <Typography>
                      {doc.fileName}
                    </Typography>
                  </Box>
                  <Button
                    variant="contained"
                    color="primary"
                    href={doc.filePath}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    DOWNLOAD
                  </Button>
                </Box>
              ))}
            </Box>

            <Box sx={{
              p: 1.5,
              bgcolor: "#f5f5f5",
              display: "flex",
              justifyContent: "center",
              borderBottomLeftRadius: 4,
              borderBottomRightRadius: 4
            }}>
              <Button
                variant="contained"
                color="error"
                onClick={() => setOpenModal(false)}
              >
                CLOSE
              </Button>
            </Box>
          </Box>
        </Box>
      )}
      <ToastContainer position="top-right" autoClose={3000} />
    </Box>

  );
};

export default SignOnModal;